// Contracts — datoric WRITES (2026-08-02): adding contributors, editing cards,
// moving people between admins, staff management, sync status.
import { z } from 'zod';
import {
  DatoricCentsSchema,
  DatoricContributorStatusSchema,
  DatoricEmailSchema,
  DatoricRoleSchema,
} from './enums';

// Datoric answers 409 when an email belongs to another provider, and their docs
// do not say whether the whole batch is rejected. Small batches keep one bad
// address from taking the rest down with it.
export const DATORIC_ROSTER_BATCH = 25;

// POST /api/datoric/contributors — add to MY pool and push upstream.
export const DatoricAddContributorsReqSchema = z.object({
  emails: z.array(DatoricEmailSchema).min(1).max(DATORIC_ROSTER_BATCH),
  name: z.string().trim().max(200).optional(),
  country: z.string().trim().length(2).optional(),
  rateCents: DatoricCentsSchema.min(0).max(100_000).optional(),
});
export type DatoricAddContributorsReq = z.infer<typeof DatoricAddContributorsReqSchema>;

/**
 * Roster delivery is reported verbatim, not as a boolean.
 *
 * 201 = saved and submitted, 202 = saved but queued for retry, 503 = saved but
 * the retry wave could not be created. All three mean "we have it", with
 * decreasing confidence that Datoric does — collapsing them would hide a roster
 * that never shipped.
 */
export const DatoricUpstreamResultSchema = z.object({
  httpStatus: z.number().int(),
  accepted: z.number().int(),
  rosterName: z.string().nullable(),
});

export const DatoricAddContributorsResSchema = z.object({
  created: z.array(z.string()),
  existing: z.array(z.string()),
  // Cabinet credentials, ONE-TIME (0004): generated for every created email and
  // for existing ones that had no password yet. Existing accounts with a
  // password are not touched — regenerating would lock people out silently.
  accounts: z.array(z.object({ email: z.string(), password: z.string() })),
  upstream: DatoricUpstreamResultSchema.nullable(),
  upstreamError: z.string().nullable(),
});
export type DatoricAddContributorsRes = z.infer<typeof DatoricAddContributorsResSchema>;

// PATCH /api/datoric/contributor/:id — edit the card. All fields optional;
// explicit null clears. rateCents null means "unset", which blocks accrual
// rather than falling back to a guessed default.
export const DatoricPatchContributorReqSchema = z.object({
  name: z.string().trim().max(200).nullable().optional(),
  phone: z.string().trim().max(50).nullable().optional(),
  country: z.string().trim().length(2).nullable().optional(),
  note: z.string().trim().max(2000).nullable().optional(),
  rateCents: DatoricCentsSchema.min(0).max(100_000).nullable().optional(),
  status: DatoricContributorStatusSchema.optional(),
  // Cabinet password reset by the pool's admin (0004). No self-service.
  password: z.string().min(8).max(200).optional(),
});
export type DatoricPatchContributorReq = z.infer<typeof DatoricPatchContributorReqSchema>;

export const DatoricOkChangedResSchema = z.object({
  ok: z.literal(true),
  changed: z.number().int(),
});
export type DatoricOkChangedRes = z.infer<typeof DatoricOkChangedResSchema>;

// POST /api/datoric/assign — move a contributor between admins (manager only).
export const DatoricAssignReqSchema = z.object({
  contributorId: z.string().min(1).max(64),
  userId: z.string().min(1).max(64),
});
export type DatoricAssignReq = z.infer<typeof DatoricAssignReqSchema>;

export const DatoricAssignResSchema = z.object({
  ok: z.literal(true),
  // false when the person already belonged to that admin — the UI may fire the
  // call idempotently.
  changed: z.boolean(),
});
export type DatoricAssignRes = z.infer<typeof DatoricAssignResSchema>;

// GET /api/datoric/unassigned — people the sync discovered with no owner.
// They are invisible to admins by design: silently dropping someone into a pool
// would be worse than showing managers a gap to claim.
export const DatoricUnassignedRowSchema = z.object({
  id: z.string(),
  email: z.string(),
  name: z.string().nullable(),
  status: DatoricContributorStatusSchema,
  created_at: z.number().int(),
  accepted_seconds: z.number().int(),
});

export const DatoricUnassignedResSchema = z.object({
  rows: z.array(DatoricUnassignedRowSchema),
});
export type DatoricUnassignedRes = z.infer<typeof DatoricUnassignedResSchema>;

// ── staff (manager only) ────────────────────────────────────────────────────

export const DatoricStaffRowSchema = z.object({
  id: z.string(),
  email: z.string(),
  name: z.string(),
  role: DatoricRoleSchema,
  // What WE pay this person per accepted hour of their pool (0002). Null =
  // not negotiated yet — and then the cascade gate blocks their rate edits.
  rate_cents: z.number().int().nullable(),
  show_balance: z.number().int(),
  disabled: z.number().int(),
  pool_size: z.number().int(),
});

export const DatoricStaffListResSchema = z.object({ rows: z.array(DatoricStaffRowSchema) });
export type DatoricStaffListRes = z.infer<typeof DatoricStaffListResSchema>;

export const DatoricCreateStaffReqSchema = z.object({
  email: DatoricEmailSchema,
  name: z.string().trim().min(1).max(200),
  role: DatoricRoleSchema,
  // Required while password login is the only door (0003): an account created
  // without one would simply be unable to sign in.
  password: z.string().min(8).max(200),
});
export type DatoricCreateStaffReq = z.infer<typeof DatoricCreateStaffReqSchema>;

export const DatoricCreateStaffResSchema = z.object({ id: z.string(), email: z.string() });
export type DatoricCreateStaffRes = z.infer<typeof DatoricCreateStaffResSchema>;

export const DatoricPatchStaffReqSchema = z.object({
  role: DatoricRoleSchema.optional(),
  showBalance: z.boolean().optional(),
  disabled: z.boolean().optional(),
  // Manager-only in the handler. Null clears the rate and re-arms the cascade
  // gate for that admin.
  rateCents: DatoricCentsSchema.min(0).max(100_000).nullable().optional(),
  // Password reset by the manager — the only reset path while email is out.
  password: z.string().min(8).max(200).optional(),
});
export type DatoricPatchStaffReq = z.infer<typeof DatoricPatchStaffReqSchema>;

// GET /api/datoric/stats/admins — the manager screen: one row per staff member
// with their pool's standing and the money split under the cascade model.
//
// Numbers are LIVE (current rates × current hours) — the quick answer to "how
// are my admins doing". The ledger is the durable, snapshot-based record; the
// two agree only while rates stay unchanged, which is exactly why both exist.
export const DatoricAdminStatsRowSchema = z.object({
  id: z.string(),
  name: z.string(),
  role: DatoricRoleSchema,
  rateCents: z.number().int().nullable(),
  disabled: z.boolean(),
  poolSize: z.number().int(),
  acceptedHours: z.number(),
  pendingHours: z.number(),
  rejectedHours: z.number(),
  // Σ contributor rate × accepted hours over pool members WITH a rate.
  contributorPayoutCents: z.number().int(),
  // Σ (admin rate − contributor rate) × accepted hours; null while the admin
  // has no rate — "no margin yet" must not read as "zero margin".
  marginCents: z.number().int().nullable(),
  // Pool members without a rate: their hours earn nobody anything, which is a
  // problem to fix, not a number to hide.
  unratedCount: z.number().int(),
});

export const DatoricAdminStatsResSchema = z.object({
  rows: z.array(DatoricAdminStatsRowSchema),
});
export type DatoricAdminStatsRes = z.infer<typeof DatoricAdminStatsResSchema>;
export type DatoricAdminStatsRow = z.infer<typeof DatoricAdminStatsRowSchema>;

// GET /api/datoric/sync/status — last run per kind for the ops corner.
export const DatoricSyncRunSchema = z.object({
  kind: z.string(),
  started_at: z.number().int(),
  finished_at: z.number().int().nullable(),
  status: z.string(),
  rows_seen: z.number().int(),
  rows_written: z.number().int(),
  error: z.string().nullable(),
});

export const DatoricSyncStatusResSchema = z.object({ runs: z.array(DatoricSyncRunSchema) });
export type DatoricSyncStatusRes = z.infer<typeof DatoricSyncStatusResSchema>;

// POST /api/datoric/_admin/bootstrap {email,name?} — X-Admin-Secret, no session.
// Solves the chicken-and-egg: someone has to create the first manager before
// any login exists. Idempotent — re-running promotes the existing row.
export const DatoricBootstrapReqSchema = z.object({
  email: DatoricEmailSchema,
  name: z.string().trim().min(1).max(200).optional(),
  // Lets the operator seed the first manager's password in the same call —
  // there is no one in the UI yet to do it, same chicken-and-egg as the role.
  password: z.string().min(8).max(200).optional(),
});
export type DatoricBootstrapReq = z.infer<typeof DatoricBootstrapReqSchema>;
